import React from 'react';
import './page.css'
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { MdSecurity, MdLock } from "react-icons/md";
import { FaShieldAlt, FaDatabase, FaUserShield, FaCookie } from "react-icons/fa";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import { GoDotFill } from "react-icons/go";
import { CiMail } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";
import { BiTime } from "react-icons/bi";

const PrivacyPolicy = () => {
  return (
    <>
      <div id="privacy" className="bg-light">
        <section className="refund-policy-page">
          <div className="policy-header">
            <div className="container">
              <h1>
                <FaShieldAlt />&nbsp;Privacy Policy
              </h1>
              <p className='text-start px-4'>
                At CodeFlex, your privacy is important to us. This policy explains how we collect, use, and protect your information when you use our software, websites, and services.
              </p>
            </div>
          </div>

          <div className="container pb-5">
            <div className="alert-box">
              <div className="alert-box-header">
                <h3><AiOutlineExclamationCircle />&nbsp;Your Privacy Matters</h3>
              </div>
              <p className='text-start px-4'>
                Please review our Privacy Policy to understand how we handle your personal information and protect your data.
              </p>
            </div>

            {/* Information We Collect Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered">
                <h2>Information We Collect</h2>
              </div>
              <p className="section-description px-1">
                We may collect the following types of information:
              </p>

              <div className="sub-section sub-section-blue">
                <h3>Personal Information</h3>
                <p>
                  Your name, email address, company name, and contact details when you register or communicate with us.
                </p>
              </div>

              <div className="sub-section sub-section-green">
                <h3>Technical Data</h3>
                <p>
                  IP address, device information, and browser type when you access our websites or use our software.
                </p>
              </div>

              <div className="sub-section">
                <h3>Usage Data</h3>
                <p>
                  Information related to how you interact with our applications and services, used to improve performance and security.
                </p>
              </div>
            </div>

            {/* How We Use Your Information Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered purple">
                <h2>How We Use Your Information</h2>
              </div>
              <p className="section-description px-1">
                We use collected data to:
              </p>

              <ul className="exception-list">
                <li className="exception-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <p><GoDotFill />&nbsp;Deliver and maintain our software and services.</p>
                </li>
                <li className="exception-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <p><GoDotFill />&nbsp;Provide customer support and respond to inquiries.</p>
                </li>
                <li className="exception-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <p><GoDotFill />&nbsp;Improve our offerings and user experience.</p>
                </li>
                <li className="exception-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <p><GoDotFill />&nbsp;Send important updates, technical notices, or marketing communications (you can opt out anytime).</p>
                </li>
                <li className="exception-item">
                  <i className="bi bi-check-circle-fill"></i>
                  <p><GoDotFill />&nbsp;Comply with legal or contractual obligations.</p>
                </li>
              </ul>
            </div>

            {/* Data Protection Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered green">
                <h2>Data Protection</h2>
              </div>

              <div className="service-card blue">
                <i className="bi bi-shield-check"></i>
                <div className="service-card-content">
                  <h3><MdSecurity /><br /><br />Security Measures</h3>
                  <p>We implement strong security measures — including encryption, firewalls, and access controls — to protect your data.</p>
                </div>
              </div>

              <div className="warning-alert">
                <i className="bi bi-exclamation-triangle-fill"></i>
                <p>However, no online system can guarantee absolute security, and we encourage users to follow good cybersecurity practices.</p>
              </div>
            </div>

            {/* Data Sharing Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered">
                <h2>Data Sharing</h2>
              </div>
              <p className="section-description px-1">
                We do not sell or rent your personal data. We may share it only with:
              </p>

              <div className="info-box neutral">
                <i className="bi bi-people"></i>
                <div className="info-box-content">
                  <p>
                    <IoMdCheckmarkCircleOutline />&nbsp;Trusted third-party service providers who help us deliver our services (e.g., hosting, payment, or analytics partners).
                  </p>
                </div>
              </div>

              <div className="info-box neutral">
                <i className="bi bi-file-earmark-text"></i>
                <div className="info-box-content">
                  <p>
                    <IoMdCheckmarkCircleOutline />&nbsp;Legal authorities, if required by law or to protect our rights.
                  </p>
                </div>
              </div>
            </div>

            {/* Cookies and Tracking Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered purple">
                <h2>Cookies and Tracking</h2>
              </div>
              
              <div className="service-card purple">
                <i className="bi bi-cookie"></i>
                <div className="service-card-content">
                  <h3><FaCookie /><br /><br />Cookie Usage</h3>
                  <p>Our website and applications may use cookies or analytics tools to enhance functionality and gather usage statistics. You can control cookie settings through your browser.</p>
                </div>
              </div>
            </div>

            {/* Data Retention Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered">
                <h2>Data Retention</h2>
              </div>
              
              <div className="service-card blue">
                <i className="bi bi-clock-history"></i>
                <div className="service-card-content">
                  <h3><BiTime /><br /><br />Retention Period</h3>
                  <p>We retain your personal data only as long as necessary to fulfill our business purposes or comply with legal requirements.</p>
                </div>
              </div>
            </div>

            {/* Your Rights Section */}
            <div className="policy-section text-start">
              <div className="section-title-bordered green">
                <h2>Your Rights</h2>
              </div>
              
              <div className="service-card purple">
                <i className="bi bi-person-check"></i>
                <div className="service-card-content">
                  <h3><FaUserShield /><br /><br />Data Access & Control</h3>
                  <p>You may request access, correction, or deletion of your personal data by contacting us at privacy@codeflexofficial.com.</p>
                </div>
              </div>
            </div>

            {/* Updates Section */}
            <div className="processing-section text-start">
              <i className="bi bi-arrow-clockwise"></i>
              <div className="processing-content">
                <h2>Updates to This Policy</h2>
                <p>We may update this policy periodically. Any changes will be posted on this page with a revised "last updated" date.</p>
              </div>
            </div>

            {/* Contact Section */}
            <div className="contact-section text-start">
              <i className="bi bi-envelope-fill main-icon"></i>
              <h2><CiMail />&nbsp;Contact Us</h2>
              <div className='px-5'>
                <p>If you have questions about our privacy practices, please contact:</p>
                <a href="mailto:privacy@codeflexofficial.com" className="contact-email-btn">
                  privacy@codeflexofficial.com
                </a>
                <br/><br/>
                <a
                  href="https://wa.me/94712129185"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-email-btn"
                  style={{ color: "#25D366" }}
                >
                  <FaWhatsapp />&nbsp;+94 71 21 29 185
                </a>
              </div>
            </div>

            <div className="text-center mt-4">
              <p style={{ color: '#64748b', fontSize: '0.9rem' }}>
                Last Updated: October 13, 2025
              </p>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default PrivacyPolicy;